import { useEffect, useState } from "react";
import { useMyProviderDataClient } from "@/context/ApiContext";

// Shapes returned by GET /EprAdmin/Quarter (EprAdminQuarterDTO). The API checks the caller's
// epr_metrics_admin permission itself and answers 403 without it.

export interface EprAdminMetric {
  key: string;
  name: string;
  sortOrder: number;
  unit: "Percent" | "Days";
  targetRule: "Value" | "AllItems";
  targetMin: number | null;
  targetMax: number | null;
  targetLabel: string;
  isComingSoon: boolean;
  description: string | null;
  explanation: string | null;   // legend text, one point per line
}

export interface EprAdminMetricValue {
  value: number | null;
  meetsTarget: boolean | null;
  itemCount: number | null;            // AllItems metrics: items judged, and how many met
  itemsMeetingTarget: number | null;
}

export interface EprAdminUser {
  username: string;
  fullName: string | null;
  role: string | null;
  region: string | null;
  status: string | null;
  hoursRequired: number | null;
  hoursBilled: number | null;
  totalHours: number | null;
  proratedHoursRequired: number | null;
  percentageComplete: number | null;
  averageHoursPerWeek: number | null;
  parachute: boolean;
  autoParachuted: boolean;
  parachuteReason: string | null;
  quarterlyBonus: number | null;
  quarterlyBonusEarned: number | null;
  bonusPaid: boolean;
  notes: string | null;
  metricsTotal: number | null;
  metricsApplicable: number | null;
  metricsMet: number | null;
  allMetricsMet: boolean | null;
  score: number | null;              // reweighted: met / applicable x 100
  requiredScore: number | null;
  meetsRequirement: boolean | null;
  metricsCalculatedAt: string | null;
  eprSyncedAt: string | null;        // when EPR_GenerateRows last updated the row
  metricsBehindEpr: boolean;         // metricsCalculatedAt is older than eprSyncedAt
  metricValues: Record<string, EprAdminMetricValue>;
}

// the quarter as a whole
export interface EprAdminSync {
  lastEprSyncAt: string | null;
  lastMetricsCalculatedAt: string | null;
  metricsBehindEpr: boolean;
  lastMetricsRunStatus: "Running" | "Succeeded" | "Failed" | null;   // null = never run
  lastMetricsRunStartedAt: string | null;
  lastMetricsRunFinishedAt: string | null;
}

export interface EprAdminQuarter {
  year: number;
  quarter: number;
  metrics: EprAdminMetric[];
  users: EprAdminUser[];
  sync: EprAdminSync;
}

export const useEprAdminQuarter = (year?: string, quarter?: number, enabled: boolean = true) => {
  const client = useMyProviderDataClient();

  const [data, setData] = useState<EprAdminQuarter | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!enabled || !year || !quarter) return;

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(undefined);
        const res = await client.get("/EprAdmin/Quarter", { params: { year, quarter } });
        if (!cancelled) setData(res.data);
      } catch (err: any) {
        console.error("EPR admin load failed", err);
        if (!cancelled) {
          setData(null);
          setError(err?.response?.status === 403
            ? "You don't have permission to view this data."
            : "Unable to load EPR and metrics.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    // a quarter picked while the previous one is still loading must not be overwritten by it
    return () => {
      cancelled = true;
    };
  }, [year, quarter, enabled, client]);

  return { data, loading, error };
};
